import { Button } from "design-system/atoms/Button"
import { DialogFooter } from "design-system/atoms/Dialog"
import { Label } from "design-system/atoms/Label"
import { Switch } from "design-system/atoms/Switch"
import { SECONDS_TO_MILLIS } from "lopermut"
import { useCampaignReachQuery } from "modules/campaigns/hooks/useCampaignReachQuery"
import { useCancelCampaignLaunchMutation } from "modules/campaigns/hooks/useCancelCampaignLaunchMutation"
import { useCreateCampaignLaunchMutation } from "modules/campaigns/hooks/useCreateCampaignLaunchMutation"
import { useLaunchAudienceLabel } from "modules/campaigns/hooks/useLaunchAudienceLabel"
import { formatLaunchTime, isUpcoming } from "modules/campaigns/lib/launchSchedule"
import { Loading } from "modules/shared/components/Loading"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { CampaignLaunchAudience } from "schema"
import { toast } from "sonner"
import { LaunchCampaignReachContent } from "../LaunchCampaign/LaunchCampaignReachContent"
import { LaunchScheduleInput } from "./LaunchScheduleInput"

type Props = {
  onClose: () => void
  onChangeAudience?: () => void
  campaignId: string
  audienceSource: CampaignLaunchAudience
}

export function ManualLaunchReach({
  onClose,
  onChangeAudience,
  campaignId,
  audienceSource,
}: Props) {
  const { t, i18n } = useTranslation()
  const { describeAudience } = useLaunchAudienceLabel()
  const [closeOngoingConversations, setCloseOngoingConversations] = useState(false)
  const [isScheduled, setIsScheduled] = useState(false)
  const [pickedScheduledAt, setPickedScheduledAt] = useState<Date>()

  const { mutateAsync: createCampaignLaunch, isPending: isLaunchingCampaign } =
    useCreateCampaignLaunchMutation(campaignId)
  const { mutateAsync: cancelCampaignLaunch } = useCancelCampaignLaunchMutation(campaignId)

  const { data, isPending } = useCampaignReachQuery({
    id: campaignId,
    enabled: true,
    query: { audienceSource, closeOngoingConversations },
  })

  const scheduledAt = isScheduled ? pickedScheduledAt : undefined
  const hasValidSchedule = Boolean(scheduledAt && isUpcoming(scheduledAt))

  async function handleLaunchCampaign() {
    const launch = await createCampaignLaunch({
      audienceSource,
      closeOngoingConversations,
      scheduledAt: scheduledAt?.toISOString(),
    })

    // Every customer in the audience gets a trigger. Reach is a projection, so it would understate
    // what the launch actually queued.
    const count = data?.audience ?? 0

    if (scheduledAt) {
      toast.success(
        t("campaigns.launchManual.scheduledSuccess", {
          count,
          date: formatLaunchTime(scheduledAt, i18n.language),
        }),
      )
    } else {
      toast.success(t("campaigns.launchManual.success", { count }), {
        duration: 10 * SECONDS_TO_MILLIS,
        action: {
          label: t("shared.undo"),
          onClick: async () => {
            await cancelCampaignLaunch(launch.id)
            toast.success(t("campaigns.scheduledLaunch.cancelSuccess"))
          },
        },
      })
    }

    onClose()
  }

  if (isPending || !data) return <Loading />

  return (
    <>
      <LaunchCampaignReachContent
        reach={data.reach}
        audience={data.audience}
        optedOutCount={data.optedOutCount}
        isEstimate={data.isEstimate}
      />
      <div className="flex items-start gap-3 rounded-lg border bg-muted p-3">
        <div className="flex min-w-0 flex-1 flex-col">
          <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            {t("campaigns.launchAudience.audience")}
          </span>
          <span className="text-sm font-medium break-words">
            {describeAudience(audienceSource)}
          </span>
        </div>
        {onChangeAudience && (
          <Button variant="link" size="sm" onClick={onChangeAudience}>
            {t("campaigns.launchAudience.change")}
          </Button>
        )}
      </div>
      <div className="flex flex-col gap-4 rounded-lg border bg-background p-4">
        <div className="flex items-center justify-between gap-4">
          <div className="flex flex-col gap-1">
            <Label htmlFor="schedule-launch" className="cursor-pointer">
              {t("campaigns.launchManual.schedule")}
            </Label>
            <p className="text-sm text-muted-foreground">
              {t("campaigns.launchManual.scheduleDescription")}
            </p>
          </div>
          <Switch
            id="schedule-launch"
            checked={isScheduled}
            disabled={isLaunchingCampaign}
            onCheckedChange={setIsScheduled}
          />
        </div>
        {isScheduled && (
          <LaunchScheduleInput
            id="launch-scheduled-at"
            defaultValue={pickedScheduledAt}
            disabled={isLaunchingCampaign}
            onChange={setPickedScheduledAt}
          />
        )}
        {isScheduled && audienceSource.kind === "rules" && (
          <p className="text-sm text-muted-foreground">
            {t("campaigns.launchAudience.savedNow", { count: data.audience })}
          </p>
        )}
      </div>
      <div className="flex items-center justify-between gap-4 rounded-lg border bg-background p-4">
        <div className="flex flex-col gap-1">
          <Label htmlFor="close-ongoing-conversations" className="cursor-pointer">
            {t("campaigns.launchManual.closeOngoingConversations")}
          </Label>
          <p className="text-sm text-muted-foreground">
            {t("campaigns.launchManual.closeOngoingConversationsDescription")}
          </p>
        </div>
        <Switch
          id="close-ongoing-conversations"
          checked={closeOngoingConversations}
          disabled={isLaunchingCampaign}
          onCheckedChange={setCloseOngoingConversations}
        />
      </div>
      <DialogFooter className="pt-4">
        <Button variant="secondary" onClick={onClose}>
          {t("shared.cancel")}
        </Button>
        <Button
          onClick={handleLaunchCampaign}
          disabled={
            isPending ||
            isLaunchingCampaign ||
            data.audience === 0 ||
            (isScheduled && !hasValidSchedule)
          }
        >
          {isScheduled
            ? t("campaigns.launchManual.scheduleLaunch", { count: data.audience })
            : t("campaigns.launchManual.launch", { count: data.audience })}
        </Button>
      </DialogFooter>
    </>
  )
}
